import angular from "angular";

import {
  ActionExecutor, ApiItemType, BootstrapStyle, Calculations, Datasets,
  JsonSchemaUtils, Payrolls, Usecase, UsecaseAction, UsecaseActionType, UsecaseStatus, UsecaseListItem,
  Usecases, UsecaseView, Translations, ODataResult
} from "@salaxy/core";

import { SessionService, UiHelpers, UsecaseApiCrudObject, UsecasesService } from "../../services";
import { ApiCrudObjectController } from "../bases";
import { AjaxNg1 } from "../../ajax";

/**
 * Usecases controller.
 */
export class UsecasesCrudController extends ApiCrudObjectController<Usecase, UsecaseListItem> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "Usecases",
    "UiHelpers",
    "$location",
    "$routeParams",
    "UsecasesService",
    "SessionService",
    "AjaxNg1",
    "$timeout",
    "Calculations",
    "Payrolls",
    "Datasets",
    "$templateCache",
  ];

  /** Action executor for the current action. */
  public currentExecutor: ActionExecutor;

  /** The current item that is being executed on. If not set, will be set to blank. */
  public currentItem: UsecaseApiCrudObject;

  /**
   * Current view
   */
  private _currentView: UsecaseView;

  /** Temporary collection of items: Will be replaced by OData query and/or settings. */
  public localStorageItems: Usecase[];

  private _currentAction: UsecaseAction;

  private _currentSchema: {
    key?: string,
    schema?: any,
  } = {};

  private _list: UsecaseListItem[];

  /**
   * Creates a new UsecasesCrudController
   * @ignore
   */
  constructor(
    usecases: Usecases,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private usecasesService: UsecasesService,
    private sessionService: SessionService,
    private ajax: AjaxNg1,
    private $timeout: angular.ITimeoutService,
    private calculations: Calculations,
    private payrolls: Payrolls,

    private datasets: Datasets,
    private $templateCache: angular.ITemplateCacheService,
  ) {
    super(usecases, uiHelpers, $location, $routeParams);
  }

  /**
   * Implement IController
   */
  public $onInit() {
    super.$onInit();
  }

  /** Gets the type name of item type, i.e. "Calculation" for "calculation" enumeration. */
  public get itemType() {
    return "CalculationListItem[]";
    // return ApiLogic.getType(this.current?.itemType);
  }

  /**
   * Shows the detail view for the item.
   * Supports viewing public items from other users as read-only.
   * @param item Item may be either Container or list item.
   */
  public showDetails(item: UsecaseListItem | Usecase): angular.ILocationService {
    // TODO: Consider adding this support in super
    if (this.parentController) {
      return this.parentController.showDetails(item);
    }
    return this.$location.url(this.getDefaults().detailsUrl + this.getId(item) + "@" + item.owner);
  }

  /**
   * Gets the activity type of usecase from the settings.
   * @param item List item to check.
   * @returns Information on whether the usecase is active or not - or default usecase (also active).
   */
  public getActive(item: UsecaseListItem): UsecaseStatus {
    return this.usecasesService.getActive(item.uri);
  }

  /**
   * Gets the activity bg-color of usecase from the settings.
   * @param status Status for color.
   * @returns Bg color for the status.
   */
    public getStatusBgColor(status: UsecaseStatus): string {
      switch( status ) {
        case UsecaseStatus.Inactive:
          return "bg-warning";
        case UsecaseStatus.Active:
          return "bg-info";
        case UsecaseStatus.Default:
          return "bg-danger";
        case UsecaseStatus.Undefined:
        default:
          return "bg-default";
      }
    }

  /**
   * Gets the order  of usecase from the settings.
   * @param item List item to get the order.
   * @returns Order number for the usecase, 0 if not set.
   */
   public getOrder = (item: UsecaseListItem): number => {
    return this.usecasesService.getOrder(item.uri);
  };

  private toLowerCamelCase(str) {
    // TODO: Move to core
    return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
  }

  /**
   * Shows the detail view with a new item that is a copy of the given item.
   * Supports copying public items from other users.
   * @param item Item may be either Container or list item.
   */
  public showCopyAsNew(item: UsecaseListItem | Usecase): angular.ILocationService {
    // TODO: Consider adding this support in super
    if (this.parentController) {
      return this.parentController.showCopyAsNew(item);
    }
    return this.$location.url(this.getDefaults().detailsUrl + this.getId(item) + "@" + item.owner + "/copy-as-new");
  }

  /** The data container in the current business object */
  public get usecase(): Usecase {
    return this.currentItem?.usecase;
  }

  /** The data part of the usecase: Used in data binding. */
  public get data(): any {
    return this.currentItem?.usecase?.data;
  }

  /**
   * Gets a list of usecases for the current account.
   * First get will start the load process and then the list is cached to controller scope.
   */
  public get list(): UsecaseListItem[] {
    if (!this._list) {
      this._list = [];
      this.getOData(null).then((result: ODataResult<UsecaseListItem>) => {
        this._list = result.items.sort((a, b) => (a.data.order || 100) - (b.data.order || 100));
      });
    }
    return this._list;
  }

  /**
   * Checks if the given schema is defined in the current usecase: Exists and has any properties.
   * @param schemaNames One or several names of the schema to check whether they are defined.
   * If any schema is exists and has properties, returns true.
   */
  public isDefined(...schemaNames: string[]): boolean {
    return JsonSchemaUtils.isDefined(this.current?.schema, ...schemaNames);
  }

  /** Returns the whole Open API schema document as editable string. */
  public get editableSchema(): string {
    return JSON.stringify(this.current.schema || "{}", null, "\t");
  }
  /** Parses the schema from the given string. */
  public set editableSchema(value: string) {
    this.current.schema = JSON.parse(value);
  }

  /** Sets the current editable schema (one of the schemas within the Open API document). */
  public setCurrentSchema(key: string): void {
    this._currentSchema.key = key;
    this._currentSchema.schema = this.current.schema.components.schemas[key];
  }

  /** Gets the current editable schema (one of the schemas within the Open API document). */
  public get currentSchema(): any {
    return this._currentSchema.schema;
  }

  /** Gets the property name / key of the current editable schema (one of the schemas within the Open API document). */
  public get currentSchemaKey(): string {
    return this._currentSchema.key;
  }

  /** Creates a new current editable schema (one of the schemas within the Open API document). */
  public createSchema() {
    // TODO: Add this to UI Helpers (as dialog) and search for prompt().
    const name = window.prompt(Translations.get("SALAXY.NG1.UsecaseDetailsComponent.createSchemaName"));
    // TODO: Add property name validation / fix.
    this._currentSchema.key = name;
    this._currentSchema.schema = {
      type: "object",
      title: `${Translations.get("SALAXY.NG1.UsecaseDetailsComponent.createSchemaTitle")} (${name})`,
      properties: {},
    };
    this.current.schema.components.schemas[name] = this._currentSchema.schema;
  }

  /** Removes an schema from this usecase. */
  public deleteSchema(key: string) {
    delete this.current.schema.components.schemas[key];
    if (this.currentSchemaKey == key) {
      this._currentSchema.key = Object.keys(this.current.schema.components.schemas)[0];
      this._currentSchema.schema = this._currentSchema.key ? this.current.schema.components.schemas[this._currentSchema.key] : null;
    }
  }

  /** The currently selected editable action (script). */
  public get currentAction(): UsecaseAction {
    return this._currentAction;
  }
  public set currentAction(value: UsecaseAction) {
    this._currentAction = value;
    this.currentExecutor = new ActionExecutor(value);
  }

  /**
   * Current editable view.
   */
  public get currentView() {
    return this._currentView;
  }
  public set currentView(value: any) {
    const key = "salaxy-dynamic-templates/usecase-custom-view.html";
    this.$templateCache.remove(key);
    if (value?.content?.trim()) {
      this.$templateCache.put(key, value.content);
    }
    this._currentView = value;
  }

  /** Overrides the save for clear cache. */
  public save(): Promise<Usecase> {
    this.usecasesService.clearCache();
    return super.save();
  }

  /** Create a new action / script to this usecase. */
  public createAction() {
    this.currentAction = {
      actionType: UsecaseActionType.ClientScript,
      uiColor: BootstrapStyle.Default,
      uiText: Translations.get("SALAXY.NG1.UsecaseDetailsComponent.createAction"),
      content: "// Add script here."
    };
    this.current.actions.push(this.currentAction);
  }

  /** Creates a new view and sets it as current. */
  public createView() {
    this.currentView = {
      label: null,
      description: null,
      content: "<p>Create your view here.</p>",
    };
    (this.current as any).views.push(this.currentView);
  }

  /** Overrides the default reload for clearing the cache. */
  public reload(): Promise<Usecase> {
    return super.reload().then((value) => {
      this.usecasesService.setToCache(value);
      return value;
    });
  }

  /**
   * Refreshes the current usecase to cache: Used in preview.
   */
  public refreshCurrentInCache() {
    this.usecasesService.setToCache(this.current);
  }

  /** Removes an action from this usecase. */
  public deleteAction(action: UsecaseAction) {
    if (!action) {
      return;
    }
    this.current.actions.splice(this.current.actions.indexOf(action), 1);
    if (this.currentAction == action) {
      this.currentAction = this.current.actions[0];
    }
  }

  /**
   * Removes a view from this usecase.
   */
  public deleteView(view: UsecaseView) {
    if (!view) {
      return;
    }
    this.current.views.splice(this.current.views.indexOf(view), 1);
    if (this.currentView == view) {
      this.currentView = this.current.views[0];
    }
  }

  /** Executes the current action with the current item. */
  public async executeAction(action?: UsecaseAction): Promise<ActionExecutor> {
    const executor = action ? new ActionExecutor(action) : this.currentExecutor;
    await executor.execute(this.currentItem, this.sessionService.session, this.ajax);
    if (executor.isAsync) {
      this.$timeout();
    }
    return executor;
  }

  /**
   * Opens a dialog for selecting the current item for which to execute a usecase.
   * @param type Type of item to select: Select from dialog, new or null/empty.
   */
  public selectItem(type: "select" | "new" | null) {
    if (!type) {
      this.currentItem = null;
      return;
    }
    switch (this.current.itemType) {
      case ApiItemType.Calculation:
      case ApiItemType.CalculationPaid:
        if (type == "new") {
          this.currentItem = this.calculations.getBlank();
        } else {
          const calcType = this.current.itemType == ApiItemType.CalculationPaid ? "paid" : "draft";
          this.uiHelpers.openSelectCalcs(calcType, "Select a calculation for demo").then((result) => {
            if (result.action == "ok" && result.item.length > 0) {
              const loader = this.uiHelpers.showLoading();
              this.calculations.getSingle(result.item[0].id).then((calc) => {
                this.currentItem = calc;
                calc.usecase.uri = this.current.uri;
                loader.dismiss();
              });
            }
          });
        }
        return;
      case ApiItemType.PayrollDetails:
        if (type == "new") {
          this.currentItem = this.payrolls.getBlank();
        } else {
          alert("TODO: Payroll selection not yet supported.");
        }
        return;
      case ApiItemType.Dataset:
        if (type == "new") {
          this.currentItem = this.datasets.getBlank();
        } else {
          this.uiHelpers.openSelect("Select a dataset").then((listItem) => {
            if (listItem) {
              const loader = this.uiHelpers.showLoading();
              this.datasets.getSingle(listItem.id).then((payload) => {
                this.currentItem = payload;
                payload.usecase.uri = this.current.uri;
                loader.dismiss();
              });
            }
          });
        }
        return;
      default:
        alert(`Item type ${this.current.itemType} not supported for debug data.`);
        return;
    }
  }

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/usecases",
      detailsUrl: this.detailsUrl || "/usecases/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/Usecases.html",
      oDataOptions: {},
    };
  }
}
